import type { Dispatch } from 'redux';
import * as Sentry from '@sentry/react';

// Constants
import { formNaming } from '../components/forms/NewCommunicationForm/constants';

// Utils
import { DraftOwnerMismatchError } from './draftOwnership';
import {
  COMMUNICATION_CREATION_DRAFT_KEYS,
  COMMUNICATION_CREATION_PICKS_KEY,
  COMMUNICATION_CREATION_SCOPE,
  communicationCreationOwnerId,
} from './draftScopes';
import { decompressSessionStorage } from './sessionStorage';

// State
import { deselectedRowModalTable } from '../state/actions/ui/selectRowTableModal';

/**
 * El borrador del ALTA de una comunicación («Nueva comunicación»): de qué
 * padre es, cómo se descarta lo que era de otro y la barrera del guardado.
 *
 * K3 de la batería QA del navegador (2026-11-03; spec 2026-11-02
 * wizard-draft-ownership): con la comunicación de la OT 801 a medias, abrir
 * `/work-orders/800/new-communication` a mano (o con atrás) enseñaba la miga de
 * 801 y `saveData` creaba la comunicación BAJO 801.
 */

type ReadableStore = Pick<Storage, 'getItem'>;
type WritableStore = Pick<Storage, 'getItem' | 'removeItem'>;
type CommunicationCreationCopy = Record<string, unknown>;

/** La copia del alta (`new-communication-form`), parseada; `null` si no hay o es ilegible. Nunca lanza. */
export const readCommunicationCreationDraft = (
  store: ReadableStore = sessionStorage
): CommunicationCreationCopy | null => {
  let raw: string | null;
  try {
    raw = store.getItem(formNaming.NEW_COMMUNICATION_FORM);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const copy = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    return copy && typeof copy === 'object' && !Array.isArray(copy)
      ? (copy as CommunicationCreationCopy)
      : null;
  } catch {
    return null;
  }
};

/**
 * La copia del alta SOLO si es del padre `parentId` (el `:id` de la URL): lo
 * dice su `parentOrderId`, que escribe la puerta («Nueva comunicación» de la
 * OT o la campaña → `pass-data`). De otro padre, sin padre, ilegible o sin
 * `parentId` → `null`.
 *
 * Tras `claimDraftScope(COMMUNICATION_CREATION_SCOPE, parent:<:id>)` la copia
 * ya es de la URL; esto es el cinturón.
 */
export const readOwnCommunicationCreationDraft = (
  parentId: string | undefined,
  store: ReadableStore = sessionStorage
): CommunicationCreationCopy | null => {
  if (!parentId) return null;
  const draft = readCommunicationCreationDraft(store);
  return draft?.parentOrderId === parentId ? draft : null;
};

/**
 * La ficha del padre desde la URL del alta: `/work-orders/<id>` o
 * `/work-orders/campaign/<id>`. Es a donde se vuelve cuando no hay alta de
 * este padre («sin puerta no hay asistente», Ruling 9 del alta de accidente).
 */
export const communicationCreationParentPath = (
  pathname: string,
  parentId: string
): string => {
  const parentPath = pathname.replace(/\/new-communication(?:\/.*)?$/, '');
  return parentPath && parentPath !== pathname
    ? parentPath
    : `/work-orders/${parentId}`;
};

/**
 * Suelta la OT/campaña que dejó elegida en Redux el alta ANTERIOR
 * (`selectedDataTableModal`, que redux-persist guarda en `localStorage`). Es
 * parte del borrador: el guardado la usa como padre en las pestañas 1 y 2 y
 * `CreateOT` autorrellena con ella clasificación, predefinido y capítulos. Si
 * se quedara, el alta nueva heredaría el padre del anterior.
 *
 * La llama el loader SOLO cuando cambia el dueño. La puerta (`ShowOT`) elige
 * la del padre nuevo ANTES de reclamar, así que el loader que corre detrás ya
 * ve el dueño nuevo y no la toca.
 */
export const releaseCommunicationCreationSelection = (dispatch: Dispatch): void => {
  // Solo las claves de Redux (la lista de elegidas a mano es de `sessionStorage`).
  [
    formNaming.NEW_COMMUNICATION_FORM,
    formNaming.NEW_COMMUNICATION_FORM_OT,
    formNaming.NEW_COMMUNICATION_FORM_CAMPAIGN,
  ].forEach((key) => dispatch(deselectedRowModalTable(key)));
};

/** Cierra el alta tras guardarla o encolarla: sus copias y su dueño (el contexto lo gestiona cada guardado). */
export const releaseCommunicationCreationDraft = (
  store: WritableStore = sessionStorage
): void => {
  COMMUNICATION_CREATION_DRAFT_KEYS.forEach((key) => store.removeItem(key));
  store.removeItem(COMMUNICATION_CREATION_SCOPE.ownerKey);
};

/**
 * Barrera ESTRICTA del guardado en los árboles de OT y de campaña (falla
 * CERRADO): el dueño tiene que ser `parent:<urlParentId>` —también si no hay
 * dueño— y el padre de la copia (`parentOrderId`) tiene que ser ese `:id`.
 * En la sección suelta (sin `:id`) no hay padre heredado que comprobar: el
 * usuario elige la OT en el modal, y `assertDraftOwner` ya rechaza un dueño
 * de OT.
 *
 * Complementa a `assertDraftOwner`, que deja pasar la ausencia de dueño.
 */
export const requireCommunicationCreationParent = (
  urlParentId: string | undefined,
  draft: { parentOrderId?: unknown } | null | undefined,
  store: ReadableStore = sessionStorage
): void => {
  if (!urlParentId) return;
  const expectedOwner = communicationCreationOwnerId(urlParentId);
  let owner: string | null = null;
  try {
    owner = store.getItem(COMMUNICATION_CREATION_SCOPE.ownerKey);
  } catch {
    owner = null;
  }
  const draftParentId = draft?.parentOrderId;
  if (owner === expectedOwner && draftParentId === urlParentId) return;

  const mismatch = new DraftOwnerMismatchError(
    COMMUNICATION_CREATION_SCOPE.name,
    owner !== expectedOwner
      ? owner
      : typeof draftParentId === 'string'
        ? `parent:${draftParentId}`
        : '(copia sin padre)',
    expectedOwner
  );
  Sentry.captureException(mismatch, {
    tags: { guard: `draft-owner-mismatch:${COMMUNICATION_CREATION_SCOPE.name}` },
  });
  throw mismatch;
};

// ─── La OT/campaña elegida en el modal (revisión de la Task 12a) ────────────
//
// Residuo entre pestañas del Ruling 11: la selección del modal vive en Redux y
// redux-persist la guarda en `localStorage`, que comparten las pestañas. Si la
// pestaña B pasa por la puerta de la OT 800 y la A recarga su alta de la 801,
// la A rehidrata la selección de B: el dueño y la copia siguen siendo de 801
// (la barrera pasa) y `saveData` guardaba BAJO 800. Regla: una selección solo
// vale si es la OT de la URL (`:id`, árboles de OT/campaña) o la que se eligió
// A MANO en el modal para ESTA alta (apuntada en `sessionStorage`, que es de la
// pestaña). Cualquier otra se suelta al cargar y se ignora al guardar.

/** Claves de Redux de la OT y la campaña elegidas en el modal del alta. */
export const COMMUNICATION_CREATION_SELECTION_KEYS: readonly string[] = [
  formNaming.NEW_COMMUNICATION_FORM_OT,
  formNaming.NEW_COMMUNICATION_FORM_CAMPAIGN,
];

type PicksStore = Pick<Storage, 'getItem'>;

/** Lo elegido a mano en esta alta: `{ [clave del modal]: id }`. Nunca lanza. */
export const readCommunicationCreationPicks = (
  store: PicksStore = sessionStorage
): Record<string, string> => {
  try {
    const parsed = JSON.parse(store.getItem(COMMUNICATION_CREATION_PICKS_KEY) ?? 'null');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string'
      )
    );
  } catch {
    return {};
  }
};

/**
 * Apunta que el usuario eligió `id` A MANO en el modal `modalKey` para esta
 * alta (lo llama el «Aceptar» del modal de relación). Solo las claves del alta;
 * editar y duplicar tienen las suyas.
 */
export const recordCommunicationCreationPick = (
  modalKey: string | undefined,
  id: unknown,
  store: Pick<Storage, 'getItem' | 'setItem'> = sessionStorage
): void => {
  if (!modalKey || !COMMUNICATION_CREATION_SELECTION_KEYS.includes(modalKey)) return;
  if (typeof id !== 'string' || !id) return;
  try {
    store.setItem(
      COMMUNICATION_CREATION_PICKS_KEY,
      JSON.stringify({ ...readCommunicationCreationPicks(store), [modalKey]: id })
    );
  } catch {
    /* almacén lleno o bloqueado: la selección no se da por buena al recargar */
  }
};

/**
 * Olvida lo elegido a mano en el modal `modalKey` para esta alta (lo llama la
 * papelera de la caja de relación). Sin esto, una elección vieja seguía
 * apuntada tras quitarla, y si OTRA pestaña dejaba esa misma OT en Redux, al
 * recargar se daba por buena (revisión de la Task 12a, Ruling 19). Solo las
 * claves del alta; nunca lanza.
 */
export const forgetCommunicationCreationPick = (
  modalKey: string | undefined,
  store: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = sessionStorage
): void => {
  if (!modalKey || !COMMUNICATION_CREATION_SELECTION_KEYS.includes(modalKey)) return;
  try {
    const picks = readCommunicationCreationPicks(store);
    if (!(modalKey in picks)) return;
    delete picks[modalKey];
    if (Object.keys(picks).length === 0) {
      store.removeItem(COMMUNICATION_CREATION_PICKS_KEY);
    } else {
      store.setItem(COMMUNICATION_CREATION_PICKS_KEY, JSON.stringify(picks));
    }
  } catch {
    /* almacén bloqueado: la elección se queda; la barrera del guardado sigue */
  }
};

/** ¿Vale la selección `selectionId` del modal `modalKey` para esta alta? */
export const isTrustedCommunicationSelection = (
  modalKey: string,
  selectionId: unknown,
  urlParentId: string | undefined,
  store: PicksStore = sessionStorage
): boolean => {
  if (typeof selectionId !== 'string' || !selectionId) return false;
  if (urlParentId && selectionId === urlParentId) return true;
  return readCommunicationCreationPicks(store)[modalKey] === selectionId;
};

/**
 * La selección del modal tal cual si vale para esta alta; si es de otra
 * pestaña, `{}` (se ignora y manda el padre de la URL). Las claves que no son
 * del modal de relación (la pestaña 0) pasan sin tocar.
 */
export const trustedCommunicationSelection = <T extends { id?: unknown }>(
  modalKey: string,
  selection: T | null | undefined,
  urlParentId: string | undefined,
  store: PicksStore = sessionStorage
): T | Record<string, never> => {
  if (!selection) return {};
  if (!COMMUNICATION_CREATION_SELECTION_KEYS.includes(modalKey) || !selection.id) {
    return selection;
  }
  if (isTrustedCommunicationSelection(modalKey, selection.id, urlParentId, store)) {
    return selection;
  }
  Sentry.captureMessage('Selección del modal de otra alta ignorada', {
    level: 'warning',
    tags: { guard: `foreign-selection:${COMMUNICATION_CREATION_SCOPE.name}` },
  });
  return {};
};

type ReduxLike = {
  getState: () => unknown;
  dispatch: Dispatch;
};

/**
 * Al cargar el formulario: suelta de Redux la OT/campaña que no vale para
 * esta alta (la rehidratada de OTRA pestaña). Con la de la URL o la elegida a
 * mano no hace nada. Devuelve las claves soltadas.
 *
 * Bajo una OT, el formulario vuelve a poner la del padre de la URL al montarse
 * (`NewCommunicationForm`, «BUG 24 FIX»: solo rellena si la clave está vacía).
 */
export const reconcileCommunicationCreationSelection = (
  redux: ReduxLike,
  urlParentId: string | undefined,
  store: PicksStore = sessionStorage
): string[] => {
  const state = redux.getState() as {
    selectedDataTableModal?: Record<string, { data?: { id?: unknown } } | undefined>;
  } | null;
  const released: string[] = [];
  COMMUNICATION_CREATION_SELECTION_KEYS.forEach((key) => {
    const id = state?.selectedDataTableModal?.[key]?.data?.id;
    if (id && !isTrustedCommunicationSelection(key, id, urlParentId, store)) {
      redux.dispatch(deselectedRowModalTable(key));
      released.push(key);
    }
  });
  return released;
};

type PersistorLike = {
  getState: () => { bootstrapped: boolean };
  subscribe: (callback: () => void) => () => void;
};

/**
 * Espera a que redux-persist haya rehidratado (la selección rancia llega CON la
 * rehidratación: mirar antes no la ve). En una recarga los loaders del router
 * pueden correr antes que ella. Con un plazo por si no llega nunca: entonces
 * se mira lo que haya y queda la barrera del guardado.
 */
export const whenReduxRehydrated = (
  persistor: PersistorLike,
  timeoutMs = 3000
): Promise<void> =>
  new Promise((resolve) => {
    if (persistor.getState().bootstrapped) {
      resolve();
      return;
    }
    let unsubscribe: () => void = () => undefined;
    const timer = setTimeout(() => {
      unsubscribe();
      resolve();
    }, timeoutMs);
    unsubscribe = persistor.subscribe(() => {
      if (!persistor.getState().bootstrapped) return;
      clearTimeout(timer);
      unsubscribe();
      resolve();
    });
  });
